/**
 * Shared SVG arrow-marker generation for edge components.
 *
 * Extracted so that both CustomEdge and SmartEdge render byte-identical arrow
 * heads (same shapes, same `<marker>` ids, same sizing rules). This keeps the
 * opt-in "smart" routing edge visually consistent with the default "custom"
 * edge — switching routing modes must NOT drop or alter arrowheads.
 *
 * The marker id convention is `${type}-${position}-${id}` and references use
 * `url(#${type}-${position}-${id})`, matching the original CustomEdge logic.
 */

export type ArrowType =
  | 'none'
  | 'arrow'
  | 'triangle'
  | 'triangle-hollow' // UML generalization/realization (hollow/white-filled triangle)
  | 'diamond'
  | 'diamond-filled'  // UML composition (filled diamond)
  | 'circle'
  | 'open-arrow'
  | 'block'
  | 'cross'
  | 'line'
  | 'slash'
  | 'dot'
  // ERD crow's-foot cardinality (read at the entity end of the line):
  | 'crowsfoot-one'          // exactly one  (single bar)
  | 'crowsfoot-many'         // many         (three-prong fork)
  | 'crowsfoot-zero-or-one'  // optional one (circle + bar)
  | 'crowsfoot-one-or-many'  // one or more  (bar + fork)
  | 'crowsfoot-zero-or-many' // zero or more (circle + fork)

/**
 * Generate SVG marker for arrow type.
 * Uses userSpaceOnUse to make arrow size independent of line width.
 */
export function generateArrowMarker(
  id: string,
  type: ArrowType,
  position: 'start' | 'end',
  color: string,
  opacity: number,
  size: number
) {
  if (type === 'none') return null

  // Base sizes independent of line width
  const arrowWidth = 10 * size
  const arrowHeight = 12 * size

  switch (type) {
    case 'arrow':
      return (
        <marker
          id={`${type}-${position}-${id}`}
          markerWidth={arrowWidth}
          markerHeight={arrowHeight}
          refX={position === 'end' ? arrowWidth : 0}
          refY={arrowHeight / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d={position === 'end' ? `M 0 0 L ${arrowWidth} ${arrowHeight / 2} L 0 ${arrowHeight} z` : `M ${arrowWidth} 0 L 0 ${arrowHeight / 2} L ${arrowWidth} ${arrowHeight} z`}
            fill={color}
            opacity={opacity}
          />
        </marker>
      )

    case 'triangle':
      return (
        <marker
          id={`${type}-${position}-${id}`}
          markerWidth={arrowWidth}
          markerHeight={10 * size}
          refX={position === 'end' ? arrowWidth : 0}
          refY={5 * size}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d={position === 'end' ? `M 0 0 L ${arrowWidth} ${5 * size} L 0 ${10 * size} z` : `M ${arrowWidth} 0 L 0 ${5 * size} L ${arrowWidth} ${10 * size} z`}
            fill={color}
            stroke={color}
            strokeWidth="1"
            opacity={opacity}
          />
        </marker>
      )

    case 'triangle-hollow':
      // UML generalization (inheritance) / realization: hollow (white-filled) triangle.
      return (
        <marker
          id={`${type}-${position}-${id}`}
          markerWidth={arrowWidth}
          markerHeight={10 * size}
          refX={position === 'end' ? arrowWidth : 0}
          refY={5 * size}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d={position === 'end' ? `M 0 0 L ${arrowWidth} ${5 * size} L 0 ${10 * size} z` : `M ${arrowWidth} 0 L 0 ${5 * size} L ${arrowWidth} ${10 * size} z`}
            fill="#ffffff"
            stroke={color}
            strokeWidth="1.5"
            opacity={opacity}
          />
        </marker>
      )

    case 'diamond':
    case 'diamond-filled': {
      const diamondW = 10 * size
      const diamondH = 12 * size
      // 'diamond' = hollow (aggregation); 'diamond-filled' = filled (composition).
      const filled = type === 'diamond-filled'
      return (
        <marker
          id={`${type}-${position}-${id}`}
          markerWidth={diamondW}
          markerHeight={diamondH}
          refX={position === 'end' ? diamondW : 0}
          refY={diamondH / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d={position === 'end' ? `M 0 ${diamondH / 2} L ${diamondW / 2} 0 L ${diamondW} ${diamondH / 2} L ${diamondW / 2} ${diamondH} z` : `M ${diamondW} ${diamondH / 2} L ${diamondW / 2} 0 L 0 ${diamondH / 2} L ${diamondW / 2} ${diamondH} z`}
            fill={filled ? color : 'none'}
            stroke={color}
            strokeWidth="1.5"
            opacity={opacity}
          />
        </marker>
      )
    }

    case 'circle': {
      const circleSize = 8 * size
      return (
        <marker
          id={`${type}-${position}-${id}`}
          markerWidth={circleSize}
          markerHeight={circleSize}
          refX={position === 'end' ? circleSize : 0}
          refY={circleSize / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <circle
            cx={circleSize / 2}
            cy={circleSize / 2}
            r={(circleSize / 2) - 1}
            fill="none"
            stroke={color}
            strokeWidth="1.5"
            opacity={opacity}
          />
        </marker>
      )
    }

    case 'open-arrow':
      // V-shaped open arrow (not filled)
      return (
        <marker
          id={`${type}-${position}-${id}`}
          markerWidth={arrowWidth}
          markerHeight={arrowHeight}
          refX={position === 'end' ? arrowWidth : 0}
          refY={arrowHeight / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d={position === 'end' ? `M 0 0 L ${arrowWidth} ${arrowHeight / 2} L 0 ${arrowHeight}` : `M ${arrowWidth} 0 L 0 ${arrowHeight / 2} L ${arrowWidth} ${arrowHeight}`}
            fill="none"
            stroke={color}
            strokeWidth="2"
            opacity={opacity}
          />
        </marker>
      )

    case 'block': {
      // Thick block/square arrow
      const blockW = 6 * size
      const blockH = 12 * size
      return (
        <marker
          id={`${type}-${position}-${id}`}
          markerWidth={blockW}
          markerHeight={blockH}
          refX={position === 'end' ? blockW : 0}
          refY={blockH / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <rect
            x={0}
            y={0}
            width={blockW}
            height={blockH}
            fill={color}
            opacity={opacity}
          />
        </marker>
      )
    }

    case 'cross': {
      // X shape
      const crossSize = 10 * size
      return (
        <marker
          id={`${type}-${position}-${id}`}
          markerWidth={crossSize}
          markerHeight={crossSize}
          refX={crossSize / 2}
          refY={crossSize / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <path
            d={`M 2 2 L ${crossSize - 2} ${crossSize - 2} M ${crossSize - 2} 2 L 2 ${crossSize - 2}`}
            fill="none"
            stroke={color}
            strokeWidth="2"
            opacity={opacity}
          />
        </marker>
      )
    }

    case 'line': {
      // Perpendicular bar
      const lineH = 12 * size
      return (
        <marker
          id={`${type}-${position}-${id}`}
          markerWidth={4}
          markerHeight={lineH}
          refX={2}
          refY={lineH / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <line
            x1={2}
            y1={0}
            x2={2}
            y2={lineH}
            stroke={color}
            strokeWidth="2"
            opacity={opacity}
          />
        </marker>
      )
    }

    case 'slash': {
      // Diagonal bar
      const slashSize = 10 * size
      return (
        <marker
          id={`${type}-${position}-${id}`}
          markerWidth={slashSize}
          markerHeight={slashSize}
          refX={slashSize / 2}
          refY={slashSize / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <line
            x1={2}
            y1={slashSize - 2}
            x2={slashSize - 2}
            y2={2}
            stroke={color}
            strokeWidth="2"
            opacity={opacity}
          />
        </marker>
      )
    }

    case 'dot': {
      // Small filled circle at tip
      const dotSize = 6 * size
      return (
        <marker
          id={`${type}-${position}-${id}`}
          markerWidth={dotSize}
          markerHeight={dotSize}
          refX={position === 'end' ? dotSize : 0}
          refY={dotSize / 2}
          orient="auto"
          markerUnits="userSpaceOnUse"
        >
          <circle
            cx={dotSize / 2}
            cy={dotSize / 2}
            r={dotSize / 2 - 1}
            fill={color}
            opacity={opacity}
          />
        </marker>
      )
    }

    case 'crowsfoot-one':
    case 'crowsfoot-many':
    case 'crowsfoot-zero-or-one':
    case 'crowsfoot-one-or-many':
    case 'crowsfoot-zero-or-many':
      return crowsFootMarker(id, type, position, color, opacity, size)

    default:
      return null
  }
}

/**
 * ERD crow's-foot cardinality marker. Symbols are laid out from the entity (the
 * path endpoint / refX) outward into the line: the cardinality symbol (bar for
 * "one", three-prong fork for "many") sits nearest the entity, and the optional
 * modality symbol (circle = "zero/optional") sits just behind it. The layout is
 * mirrored for start vs end markers so a single definition serves both ends.
 */
function crowsFootMarker(
  id: string,
  type: ArrowType,
  position: 'start' | 'end',
  color: string,
  opacity: number,
  size: number
) {
  const s = size
  const h = 14 * s
  const forkLen = 14 * s
  const cy = h / 2
  const shapes: any[] = []
  let W = 12 * s
  // d = distance the symbol sits from the entity, going back along the line.
  const xAt = (d: number) => (position === 'end' ? W - d : d)
  const addBar = (d: number) => {
    const x = xAt(d)
    shapes.push(
      <line key={`b${d}`} x1={x} y1={2} x2={x} y2={h - 2} stroke={color} strokeWidth={1.8} opacity={opacity} />
    )
  }
  const addCircle = (d: number) => {
    const x = xAt(d)
    shapes.push(
      <circle key={`c${d}`} cx={x} cy={cy} r={3.2 * s} fill="#ffffff" stroke={color} strokeWidth={1.5} opacity={opacity} />
    )
  }
  const addFork = () => {
    const ex = position === 'end' ? W : 0 // prong tips touch the entity
    const ax = position === 'end' ? W - forkLen : forkLen // apex points into the line
    shapes.push(
      <path
        key="fork"
        d={`M ${ax} ${cy} L ${ex} 2 M ${ax} ${cy} L ${ex} ${cy} M ${ax} ${cy} L ${ex} ${h - 2}`}
        fill="none"
        stroke={color}
        strokeWidth={1.8}
        opacity={opacity}
      />
    )
  }

  switch (type) {
    case 'crowsfoot-one':
      W = 10 * s
      addBar(3 * s)
      break
    case 'crowsfoot-zero-or-one':
      W = 18 * s
      addBar(3 * s)
      addCircle(10 * s)
      break
    case 'crowsfoot-many':
      W = 16 * s
      addFork()
      break
    case 'crowsfoot-one-or-many':
      W = forkLen + 8 * s
      addFork()
      addBar(forkLen + 4 * s)
      break
    case 'crowsfoot-zero-or-many':
      W = forkLen + 11 * s
      addFork()
      addCircle(forkLen + 6 * s)
      break
    default:
      break
  }

  return (
    <marker
      id={`${type}-${position}-${id}`}
      markerWidth={W}
      markerHeight={h}
      refX={position === 'end' ? W : 0}
      refY={cy}
      orient="auto"
      markerUnits="userSpaceOnUse"
    >
      {shapes}
    </marker>
  )
}

/**
 * Compute the marker url() references for an edge given its arrow config.
 * Mirrors the CustomEdge convention: a bidirectional edge gets a start arrow
 * even when arrowStart is 'none'.
 */
export function getArrowMarkerRefs(
  id: string,
  arrowStart: ArrowType,
  arrowEnd: ArrowType,
  bidirectional: boolean
): { markerStart?: string; markerEnd?: string } {
  const markerStart =
    bidirectional || arrowStart !== 'none'
      ? `url(#${arrowStart === 'none' ? 'arrow' : arrowStart}-start-${id})`
      : undefined

  const markerEnd =
    arrowEnd !== 'none' ? `url(#${arrowEnd}-end-${id})` : undefined

  return { markerStart, markerEnd }
}
